// API keys list. Two destructive paths each guarded by a Dialog confirm:
//   - Revoke: trash icon → Dialog with key name → Confirm fires DELETE
//   - Create: button → Dialog captures name+scopes, then on success
//             reveals the secret EXACTLY ONCE with a copy affordance.
//
// The reveal-once UX is the security-critical part. After the modal closes
// the platform cannot recover the secret — the dashboard makes that explicit
// rather than tucking it behind a "more info" link.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Plus, Trash2 } from 'lucide-react';
import {
  API_KEY_SCOPES,
  type ApiKey,
  type ApiKeyScope,
  type CreateApiKeyResult,
} from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { friendlyError } from '../../lib/error-message.js';
import { apiKeysApi, API_KEYS_QUERY_KEY } from '../../lib/api/api-keys.js';
import { formatDate } from '../../lib/format-date.js';
import { Button } from '../../components/ui/button.js';
import { Badge } from '../../components/ui/badge.js';
import { Card, CardContent } from '../../components/ui/card.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { toast } from '../../components/ui/toaster.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog.js';
import { Input } from '../../components/ui/input.js';
import { Label } from '../../components/ui/label.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';

// Default scope set: read-only chat. Operators almost always want a key to
// power a single embed/server integration, not the dashboard's full surface.
const DEFAULT_SCOPES: readonly ApiKeyScope[] = ['chat:write'];

// Plain-language labels for non-tech operators. Falls back to the raw
// scope code if a new scope is added without a label entry.
const SCOPE_LABELS: Partial<Record<ApiKeyScope, string>> = {
  'chat:write': 'Send messages (required for the widget)',
  'chat:read': 'Read past conversations',
  'agents:read': 'Read agent settings',
  'agents:write': 'Create or edit agents',
  'billing:read': 'Read usage and invoices',
  'api_keys:read': 'Read other API keys',
  'api_keys:write': 'Create or revoke API keys',
  'widget:mint': 'Allow website embeds',
  'audit:read': 'Read the audit log',
  admin: 'Full administrative access',
};

type KeyStatus = 'active' | 'revoked' | 'expired';

function statusFor(key: ApiKey): KeyStatus {
  if (key.revokedAt) return 'revoked';
  if (key.expiresAt && new Date(key.expiresAt).getTime() <= Date.now()) return 'expired';
  return 'active';
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

function lastUsedLabel(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (abs < hour) return RELATIVE.format(Math.round(ms / minute), 'minute');
  if (abs < day) return RELATIVE.format(Math.round(ms / hour), 'hour');
  if (abs < 30 * day) return RELATIVE.format(Math.round(ms / day), 'day');
  return formatDate(iso);
}

export function ApiKeysListPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [pendingRevoke, setPendingRevoke] = useState<ApiKey | null>(null);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<CreateApiKeyResult | null>(null);

  const list = useQuery({
    queryKey: API_KEYS_QUERY_KEY,
    queryFn: () => apiKeysApi.list(api),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiKeysApi.remove(api, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
      toast.success(pendingRevoke ? `Revoked ${pendingRevoke.name}` : 'API key revoked');
    },
    onError: (err) => {
      toast.error('Could not revoke key', { description: friendlyError(err) });
    },
    onSettled: () => { setPendingRevoke(null); },
  });

  const headerActions = (
    <Button onClick={() => { setCreating(true); }}>
      <Plus aria-hidden /> New key
    </Button>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="API keys"
        description="Server-side credentials for the SDK and embed snippet. Secrets are shown once."
        actions={headerActions}
      />

      {list.isLoading && <KeysSkeleton />}
      {list.isError && (
        <ErrorRetry
          title="Could not load API keys"
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      )}
      {list.data?.keys.length === 0 && (
        <EmptyState
          icon={KeyRound}
          title="No keys yet"
          description="API keys connect your website or app to Cortex. The widget needs one to load on your site; you only need to create extra keys for your own server or test environments."
          action={
            <Button onClick={() => { setCreating(true); }}>
              <Plus aria-hidden /> New key
            </Button>
          }
        />
      )}
      {list.data && list.data.keys.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12 text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.keys.map((key) => {
                  const status = statusFor(key);
                  return (
                    <TableRow key={key.id}>
                      <TableCell className="font-medium">{key.name}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {key.prefix}…{key.last4}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {key.scopes.join(', ')}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {lastUsedLabel(key.lastUsedAt)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={status} />
                      </TableCell>
                      <TableCell className="text-right">
                        {status === 'active' ? (
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`Revoke ${key.name}`}
                            onClick={() => { setPendingRevoke(key); }}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground" aria-hidden>
                            —
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CreateDialog
        open={creating && !revealed}
        onClose={() => { setCreating(false); }}
        onCreated={(result) => {
          setRevealed(result);
          setCreating(false);
          void queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
          toast.success(`Created ${result.key.name}`);
        }}
      />
      <RevealDialog
        result={revealed}
        onClose={() => { setRevealed(null); }}
      />
      <RevokeDialog
        target={pendingRevoke}
        pending={remove.isPending}
        onClose={() => { setPendingRevoke(null); }}
        onConfirm={(id) => { remove.mutate(id); }}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: KeyStatus }) {
  if (status === 'active') return <Badge variant="success">active</Badge>;
  if (status === 'revoked') return <Badge variant="muted">revoked</Badge>;
  return <Badge variant="warning">expired</Badge>;
}

function KeysSkeleton() {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="ml-auto h-4 w-20" />
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (result: CreateApiKeyResult) => void;
}

function CreateDialog({ open, onClose, onCreated }: CreateDialogProps) {
  const api = useApiClient();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<readonly ApiKeyScope[]>(DEFAULT_SCOPES);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiKeysApi.create(api, { name: name.trim(), scopes: [...scopes] }),
    onSuccess: (result) => { onCreated(result); reset(); },
    onError: (err) => {
      setError(friendlyError(err, 'Could not create key.'));
    },
  });

  function toggleScope(scope: ApiKeyScope): void {
    setScopes((current) =>
      current.includes(scope)
        ? current.filter((s) => s !== scope)
        : [...current, scope],
    );
  }

  function reset(): void {
    setName('');
    setScopes(DEFAULT_SCOPES);
    setError(null);
  }

  function submit(): void {
    setError(null);
    if (name.trim().length === 0) {
      setError('Name is required');
      return;
    }
    if (scopes.length === 0) {
      setError('Select at least one scope');
      return;
    }
    create.mutate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
          reset();
        }
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New API key</DialogTitle>
          <DialogDescription>
            Pick what this key is allowed to do. The widget on your website only needs
            &ldquo;Send messages&rdquo; and &ldquo;Allow website embeds&rdquo;.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="api-key-name">Name</Label>
            <Input
              id="api-key-name"
              value={name}
              onChange={(e) => { setName(e.target.value); }}
              placeholder="e.g. acme-prod-server"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>Permissions</Label>
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              {API_KEY_SCOPES.map((scope) => (
                <label
                  key={scope}
                  className="flex cursor-pointer items-start gap-2 rounded-md border p-3 transition-colors hover:bg-accent/40"
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope)}
                    onChange={() => { toggleScope(scope); }}
                    aria-label={scope}
                    className="mt-1 h-4 w-4 rounded border-input accent-primary"
                  />
                  <div className="flex-1 leading-tight">
                    <div className="text-sm">{SCOPE_LABELS[scope] ?? scope}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{scope}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RevealDialogProps {
  result: CreateApiKeyResult | null;
  onClose: () => void;
}

function RevealDialog({ result, onClose }: RevealDialogProps) {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    if (!result) return;
    // Some test environments (jsdom) don't ship navigator.clipboard. Fall back
    // to a no-op in that case — the secret is still visible to the user.
    try {
      await navigator.clipboard.writeText(result.secret);
      setCopied(true);
      toast.success('Copied to clipboard');
    } catch {
      setCopied(false);
    }
  }

  return (
    <Dialog
      open={result !== null}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
          setCopied(false);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save your new key</DialogTitle>
          <DialogDescription>
            {result ? (
              <>
                This is the only time we will show{' '}
                <strong className="text-foreground">{result.key.name}</strong>. Copy it somewhere
                safe — we cannot recover it for you.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        {result && (
          <pre
            aria-label="API key secret"
            className="overflow-x-auto rounded-md border bg-muted px-3 py-2 font-mono text-xs"
          >
            {result.secret}
          </pre>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => void copy()}>
            {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RevokeDialogProps {
  target: ApiKey | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (id: string) => void;
}

function RevokeDialog({ target, pending, onClose, onConfirm }: RevokeDialogProps) {
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke API key?</DialogTitle>
          <DialogDescription>
            {target ? (
              <>
                <span className="font-medium text-foreground">{target.name}</span> will stop
                working immediately. Embeds and SDK callers using it will see 401 errors until
                you swap them.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={pending || !target}
            onClick={() => {
              if (target) onConfirm(target.id);
            }}
          >
            {pending ? 'Revoking…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
